import { useApolloClient } from '@provision/graphql-client';
import { useEffect, useState } from 'react';
import {
  GetConversationMessageQuery,
  getConversationMessagesQueryVariables,
} from 'src/graphql/entities/conversation-message';
import { GET_CONVERSATION_MESSAGES_QUERY } from 'src/graphql/entities/conversation-message/conversation-message.operations';
import { QueryVariables } from 'src/graphql/types';

export enum TargetLookupStatus {
  Idle = 'idle',
  Loading = 'loading',
  Found = 'found',
  Missing = 'missing',
}

export interface TargetMessageLookup {
  status: TargetLookupStatus;
  createdAt: string | null;
}

interface LookupResult extends TargetMessageLookup {
  targetMessageId: string;
}

const IDLE_LOOKUP: TargetMessageLookup = { status: TargetLookupStatus.Idle, createdAt: null };
const LOADING_LOOKUP: TargetMessageLookup = {
  status: TargetLookupStatus.Loading,
  createdAt: null,
};

/*
  Asks the server whether the target is a timeline message of this conversation, and when
  it was created — the paging that brings it into the thread stops once the loaded window
  is older than that. Replies are filtered out by the query, so they come back missing.
*/
export function useTargetMessageLookup(
  conversationId: string,
  targetMessageId: string | null,
): TargetMessageLookup {
  const client = useApolloClient();
  const [result, setResult] = useState<LookupResult | null>(null);

  useEffect(() => {
    if (!targetMessageId) return;
    let ignore = false;

    const lookUp = async () => {
      try {
        const { data } = await client.query<GetConversationMessageQuery, QueryVariables>({
          query: GET_CONVERSATION_MESSAGES_QUERY,
          variables: getConversationMessagesQueryVariables({
            filters: [
              { attribute: 'conversationId', operator: '=', value: conversationId },
              { attribute: 'conversationMessageId', operator: '=', value: targetMessageId },
            ],
            offset: 0,
            limit: 1,
          }),
          // a one-record page written into the cache could merge into the thread's own list
          fetchPolicy: 'no-cache',
        });
        if (ignore) return;
        const record = data?.getConversationMessage.records[0];
        setResult({
          targetMessageId,
          status: record ? TargetLookupStatus.Found : TargetLookupStatus.Missing,
          createdAt: record?.createdAt ?? null,
        });
      } catch {
        // a malformed id is rejected by the API rather than returning no rows
        if (!ignore) {
          setResult({ targetMessageId, status: TargetLookupStatus.Missing, createdAt: null });
        }
      }
    };
    void lookUp();

    return () => {
      ignore = true;
    };
  }, [client, conversationId, targetMessageId]);

  if (!targetMessageId) return IDLE_LOOKUP;
  if (result?.targetMessageId !== targetMessageId) return LOADING_LOOKUP;
  return result;
}
